import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, Building2, MapPin, BarChart3 } from 'lucide-react';
import { routingKeyToCounty } from '../../data/routingKeyToCounty';

const countySlugs = Array.from(
  new Set(Object.values(routingKeyToCounty).map((county) => county.toLowerCase()))
).sort();

const toTitleCase = (value: string) =>
  value
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const HousingReportDiscoverPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCounties = useMemo(
    () => countySlugs.filter((county) => county.includes(searchTerm.toLowerCase().trim())),
    [searchTerm]
  );

  return (
    <>
      <Helmet>
        <title>Discover Housing Reports in Ireland | County Market Insights</title>
        <meta
          name="description"
          content="Browse county-level housing reports across Ireland. Compare median prices, sales activity, and market trends with SEO-rich local reports by county."
        />
        <meta
          name="keywords"
          content="Ireland housing report, county property report Ireland, Irish property market by county, house price report Ireland"
        />
        <link rel="canonical" href="https://rentsmart.ie/housing-reports" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'Ireland County Housing Reports',
            description: 'County-level property market reports for Ireland.',
            url: 'https://rentsmart.ie/housing-reports',
          })}
        </script>
      </Helmet>

      <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] bg-slate-50">
        <section className="relative overflow-hidden bg-gradient-to-br from-emerald-700 via-teal-700 to-sky-700 text-white px-4 pt-16 pb-20">
          <div className="absolute -top-24 -right-16 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-16 -left-16 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" />
          <div className="relative max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-xs font-semibold uppercase tracking-wider mb-5">
              <BarChart3 className="h-4 w-4" /> Ireland Property Intelligence
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-5">
              Discover County Housing Reports
            </h1>
            <p className="max-w-3xl mx-auto text-cyan-100 text-lg leading-relaxed mb-8">
              Explore county-by-county insights with median prices, trend direction, local activity,
              and practical guidance for buyers and sellers in Ireland.
            </p>

            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search county (e.g. dublin, cork, galway)"
                className="w-full pl-12 pr-4 py-4 rounded-xl bg-white text-slate-900 shadow-xl outline-none focus:ring-4 focus:ring-cyan-100"
              />
            </div>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 py-14">
          {filteredCounties.length === 0 ? (
            <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500">
              No counties match your search.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCounties.map((countySlug) => {
                const countyName = toTitleCase(countySlug);
                return (
                  <Link
                    key={countySlug}
                    to={`/housing-reports/${countySlug}`}
                    className="group block rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all no-underline"
                  >
                    <div className="p-6 border-b border-slate-100 bg-gradient-to-r from-cyan-50 to-emerald-50 rounded-t-2xl">
                      <div className="flex items-center justify-between">
                        <div className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-white border border-cyan-100 text-teal-700">
                          <Building2 className="h-5 w-5" />
                        </div>
                        <MapPin className="h-5 w-5 text-cyan-600" />
                      </div>
                      <h2 className="mt-4 text-2xl font-bold text-slate-900">{countyName}</h2>
                    </div>

                    <div className="p-6">
                      <p className="text-sm text-slate-600 leading-relaxed mb-5">
                        View housing trends, price ranges, local sales activity, and county-level
                        market narrative for {countyName}.
                      </p>
                      <div className="inline-flex items-center gap-2 font-semibold text-teal-700 group-hover:text-teal-600">
                        Open Report <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </>
  );
};

export default HousingReportDiscoverPage;
